/**
 * DonationItemSection.tsx - Donation Item Configuration Section
 *
 * This component configures product-based donations where donors can select
 * specific items or packages with quantities and pricing.
 *
 * Key Features:
 * - Donation item selection and configuration
 * - Product catalog integration
 * - Quantity-based pricing and options
 * - Item descriptions and images
 * - Inventory management and availability
 * - Custom donation item creation
 *
 * Item Configuration:
 * - Item Selection: Choose from available donation products
 * - Pricing: Set fixed prices or suggested amounts
 * - Quantities: Configure quantity options and limits
 * - Descriptions: Item details and impact information
 * - Images: Product photos and visual representation
 *
 * Product Types:
 * - Physical Items: Tangible products sent to donors
 * - Virtual Items: Digital rewards or recognition
 * - Experiences: Events, tours, or special access
 * - Impact Items: Symbolic donations (e.g., "fund a well")
 *
 * Management Features:
 * - Item search and selection interface
 * - Custom item creation and editing
 * - Inventory tracking and availability
 * - Pricing validation and formatting
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: Product catalog, item management, MobX store
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Search, XCircle, PlusCircle, Copy } from "lucide-react";
import { fundraiserStore } from "../../../stores";
import { CreateFundraiserModel, DonationItemModel } from "../../../models";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const SearchableDropdown = styled.div`
  position: relative;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  width: 20px;
  height: 20px;
`;

const SearchInput = styled.input<{ hasError?: boolean }>`
  width: 50%;
  padding: 16px 16px 16px 48px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const DropdownList = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 50%;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 12px 12px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  display: ${(props) => (props.visible ? "block" : "none")};
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f1f5f9;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownItemName = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownItemDescription = styled.div`
  font-size: 14px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AccountCard = styled.div<{ selected: boolean }>`
  padding: 16px 20px;
  border: 2px solid ${(props) => (props.selected ? "#3b82f6" : "#e2e8f0")};
  border-radius: 12px;
  background: ${(props) => (props.selected ? "#f0f9ff" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0;
  width: 50%;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const AccountCardContainer = styled.div`
  display: flex;
  align-items: center;
`;

const RemoveIconContainer = styled.div`
  margin-left: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
  }

  svg {
    color: #3b82f6;
    width: 24px;
    height: 24px;
  }
`;

const AccountHeader = styled.div`
  position: relative;
  height: 24px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const AccountName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
`;

const AccountDetails = styled.div`
  color: #64748b;
  font-size: 14px;
  line-height: 1.4;
`;

const HelpText = styled.p<{ isError?: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.isError ? "#dc2626" : "#64748b")};
  margin-top: 8px;
  line-height: 1.4;
`;

const SuggestionText = styled.span`
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.4;
`;

const SuggestionLink = styled.span`
  color: #3b82f6;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const CopyIcon = styled(Copy)`
  width: 14px;
  height: 14px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  color: #374151;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
`;

const CreateItemInputContainer = styled.div`
  position: relative;
  width: 50%;
`;

const CreateItemIcon = styled(PlusCircle)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  width: 20px;
  height: 20px;
`;

const CreateItemInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

interface DonationItemSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const DonationItemSection: React.FC<DonationItemSectionProps> = observer(
  ({ builderData, updateBuilderField, onValidationChange }) => {
    const [donationSearch, setDonationSearch] = useState("");
    const [showDonationDropdown, setShowDonationDropdown] = useState(false);
    const [selectedDonationItem, setSelectedDonationItem] = useState(
      builderData.donationItem
    );

    const [itemMode, setItemMode] = useState<"select" | "create">(
      builderData.isCreateNewDonationItem ? "create" : "select"
    );
    const [newItemName, setNewItemName] = useState(
      builderData.newDonationItemName || ""
    );
    const [selectError, setSelectError] = useState(false);
    const [createError, setCreateError] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    useEffect(() => {
      setSelectedDonationItem(builderData.donationItem);
      setItemMode(builderData.isCreateNewDonationItem ? "create" : "select");
      setNewItemName(builderData.newDonationItemName || "");
    }, [
      builderData.isCreateNewDonationItem,
      builderData.newDonationItemName,
      builderData.donationItem,
    ]);

    const donationItems = fundraiserStore.donationItems;
    const filteredDonationItems = donationItems.filter(
      (item) =>
        item.name.toLowerCase().includes(donationSearch.toLowerCase()) ||
        item.description.toLowerCase().includes(donationSearch.toLowerCase())
    );

    const isDuplicateItemName = (name: string) => {
      return donationItems.some(
        (item) => item.name.toLowerCase() === name.toLowerCase().trim()
      );
    };

    // Validate selection mode - only show errors when user has interacted
    const validateSelection = (showErrors = false) => {
      const isSelectValid = itemMode !== "select" || selectedDonationItem;
      const isCreateValid =
        itemMode !== "create" ||
        (newItemName.trim() && !isDuplicateItemName(newItemName));

      if (showErrors && hasInteracted) {
        if (itemMode === "select" && !selectedDonationItem) {
          setSelectError(true);
        }
        if (
          itemMode === "create" &&
          (!newItemName.trim() || isDuplicateItemName(newItemName))
        ) {
          setCreateError(true);
        }
      }

      return isSelectValid && isCreateValid;
    };

    //Expose validation to parent component
    useEffect(() => {
      const isValid = validateSelection() ? true : false;
      onValidationChange?.(isValid);
    }, []);

    const handleDonationItemSelect = (item: DonationItemModel) => {
      setSelectedDonationItem(item);
      setDonationSearch("");
      setShowDonationDropdown(false);
      setSelectError(false);
      setCreateError(false);
      setHasInteracted(true);
      updateBuilderField("donationItem", item);
      updateBuilderField("isCreateNewDonationItem", false);
    };

    const handleRemoveSelectedItem = () => {
      setSelectedDonationItem(null);
      setHasInteracted(true);
      // Clear the input when removing a created item
      if (itemMode === "create") {
        setNewItemName("");
      }
      // Show error immediately when clearing selection
      if (itemMode === "select") {
        setSelectError(true);
      }
      updateBuilderField("donationItem", null);
      updateBuilderField("isCreateNewDonationItem", false);
    };

    const handleCreateItem = () => {
      if (newItemName.trim() && !isDuplicateItemName(newItemName)) {
        const newItem = new DonationItemModel({
          id: Date.now().toString(), // Simple ID generation
          name: newItemName.trim(),
          description: "Custom donation item",
          // isCustom: true,
        });
        setSelectedDonationItem(newItem);
        setCreateError(false);
        setSelectError(false);
        setHasInteracted(true);
        updateBuilderField("newDonationItemName", newItemName.trim());
        updateBuilderField("isCreateNewDonationItem", true);
      }
    };

    const handleCreateItemBlur = () => {
      setHasInteracted(true);
      if (!newItemName.trim()) {
        setCreateError(true);
      } else if (isDuplicateItemName(newItemName)) {
        setCreateError(true);
      } else {
        setCreateError(false);
        handleCreateItem();
      }
    };

    const handleSearchBlur = () => {
      setHasInteracted(true);
      setTimeout(() => {
        setShowDonationDropdown(false);
        if (itemMode === "select" && !selectedDonationItem) {
          setSelectError(true);
        }
      }, 200);
    };

    const handleModeChange = (mode: "select" | "create") => {
      setItemMode(mode);
      setSelectError(false);
      setCreateError(false);
      // Don't set hasInteracted here to avoid showing errors immediately after mode change

      if (mode === "create") {
        updateBuilderField("isCreateNewDonationItem", true);
        // Clear any previously selected items
        setSelectedDonationItem(null);
        updateBuilderField("donationItem", null);
        // Don't auto-set the name, let user see the suggestion instead
        setNewItemName("");
        updateBuilderField("isCreateNewDonationItem", true);
      } else {
        updateBuilderField("isCreateNewDonationItem", false);
        // When switching to Select, clear the created item and any input
        setSelectedDonationItem(null);
        updateBuilderField("donationItem", null);
        updateBuilderField("isCreateNewDonationItem", false);
        setNewItemName("");
      }
    };

    // Check if fundraiser name can be suggested
    const canSuggestFundraiserName = () => {
      return (
        itemMode === "create" &&
        !selectedDonationItem &&
        builderData.name &&
        builderData.name.trim() !== "" &&
        !isDuplicateItemName(builderData.name)
      );
    };

    // Handle clicking on the fundraiser name suggestion
    const handleSuggestionClick = () => {
      if (builderData.name) {
        const newItem = new DonationItemModel({
          id: Date.now().toString(), // Simple ID generation
          name: builderData.name.trim(),
          description: "Custom donation item",
          // isCustom: true,
        });
        setSelectedDonationItem(newItem);
        setNewItemName(builderData.name);
        setCreateError(false);
        setSelectError(false);
        setHasInteracted(true);
        updateBuilderField("donationItem", newItem);
        updateBuilderField("isCreateNewDonationItem", true);
      }
    };

    return (
      <Section>
        <SectionTitle>Donation Item</SectionTitle>

        <FormGroup>
          <RadioGroup>
            <RadioOption>
              <RadioInput
                type="radio"
                name="itemMode"
                value="select"
                checked={itemMode === "select"}
                onChange={() => handleModeChange("select")}
              />
              Select
            </RadioOption>
            <RadioOption>
              <RadioInput
                type="radio"
                name="itemMode"
                value="create"
                checked={itemMode === "create"}
                onChange={() => handleModeChange("create")}
              />
              Create
            </RadioOption>
          </RadioGroup>

          {itemMode === "select" ? (
            !selectedDonationItem ? (
              <SearchableDropdown>
                <SearchIcon />
                <SearchInput
                  type="text"
                  placeholder="Search donation items..."
                  value={donationSearch}
                  hasError={selectError || builderData.hasError("donationItem")}
                  onChange={(e) => {
                    setDonationSearch(e.target.value);
                    if (selectError) setSelectError(false);
                  }}
                  onClick={() => {
                    setShowDonationDropdown(true);
                    if (!donationSearch) {
                      setDonationSearch("");
                    }
                  }}
                  onBlur={handleSearchBlur}
                />
                <DropdownList
                  visible={showDonationDropdown}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {filteredDonationItems.map((item) => (
                    <DropdownItem
                      key={item.id}
                      onClick={() => {
                        handleDonationItemSelect(item);
                      }}
                    >
                      <DropdownItemContent>
                        <DropdownItemName>{item.name}</DropdownItemName>
                        <DropdownItemDescription>
                          {item.description}
                        </DropdownItemDescription>
                      </DropdownItemContent>
                    </DropdownItem>
                  ))}
                </DropdownList>
              </SearchableDropdown>
            ) : null
          ) : (
            !selectedDonationItem && (
              <CreateItemInputContainer>
                <CreateItemIcon />
                <CreateItemInput
                  type="text"
                  placeholder="Type name and press enter to create..."
                  value={newItemName}
                  hasError={createError}
                  onChange={(e) => {
                    setNewItemName(e.target.value);
                    if (createError) setCreateError(false);
                    // If user clears the input, show error
                    if (!e.target.value.trim() && hasInteracted) {
                      setCreateError(true);
                    }
                  }}
                  onBlur={handleCreateItemBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateItemBlur();
                    }
                  }}
                />
              </CreateItemInputContainer>
            )
          )}

          {/* Only show error if there's actually an error AND user has interacted AND no item is selected */}
          {(builderData.hasError("donationItem") ||
            ((selectError || createError) &&
              hasInteracted &&
              !selectedDonationItem)) && (
            <HelpText isError={true}>Selecting an item is required</HelpText>
          )}

          {/* Show normal help text when no errors and no item is selected */}
          {!selectError && !createError && !selectedDonationItem && (
            <HelpText isError={false}>
              {itemMode === "select" ? (
                "Attribute donations received to an existing donation item"
              ) : (
                <>
                  Attribute donations received to a new donation item
                  {canSuggestFundraiserName() && (
                    <>
                      . Click to add{" "}
                      <SuggestionLink onClick={handleSuggestionClick}>
                        {builderData.name} <CopyIcon />
                      </SuggestionLink>
                    </>
                  )}
                </>
              )}
            </HelpText>
          )}
        </FormGroup>

        {selectedDonationItem && (
          <div style={{ marginBottom: "16px", marginTop: "8px" }}>
            <AccountCardContainer>
              <AccountCard selected={true}>
                <AccountHeader>
                  <AccountName>{selectedDonationItem.name}</AccountName>
                </AccountHeader>
                <AccountDetails>
                  {selectedDonationItem.description}
                </AccountDetails>
              </AccountCard>
              <RemoveIconContainer onClick={handleRemoveSelectedItem}>
                <XCircle />
              </RemoveIconContainer>
            </AccountCardContainer>
            {/* Show help text below the selected item */}
            <HelpText isError={false} style={{ marginTop: "12px" }}>
              {itemMode === "select"
                ? "Attribute donations received to an existing donation item"
                : "Attribute donations received to a new donation item"}
            </HelpText>
          </div>
        )}
      </Section>
    );
  }
);
